/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FileRecognitionService } from '../file-recognition-service';
import type { RecognitionResult } from '../../types/file-upload';
import { FileParseApi, type DocumentParseResponse } from '../../apis/file-parse-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FileStorageService } from '../file-storage-service';

/**
 * 基于 FileParseApi 的文件识别服务实现
 * 使用真实的文件解析API进行文件内容识别
 */
@injectable()
export class FileRecognitionServiceImpl implements FileRecognitionService {
    constructor(
        @inject(FileParseApi)
        private readonly _fileParseApi: FileParseApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FileStorageService)
        private readonly _fileStorageService: FileStorageService,
    ) {}

    /**
     * 识别文件内容
     * @param params 识别参数
     * @param params.fileId 服务器端文件ID
     * @param params.contentType 文件内容类型
     * @param onProgress 进度回调函数
     * @returns 识别结果
     */
    async recognize(
        params: { fileId: string; contentType: string },
        onProgress?: (progress: number) => void,
    ): Promise<RecognitionResult> {
        try {
            // 模拟进度回调（因为当前API不支持进度跟踪）
            await this._simulateProgress(onProgress);

            // 解析 fileId 获取任务信息
            const { taskId } = this._parseFileId(params.fileId);

            // 从存储服务获取原始文件
            const file = this._fileStorageService.getFile(params.fileId);
            if (!file) {
                throw new Error(`无法找到文件: ${params.fileId}`);
            }

            // 使用传入的内容类型
            const contentType = params.contentType as 'DISCLOSURE' | 'CLAIM';

            // 调用文件解析API
            const parseResponse = await this._fileParseApi.parseFile({
                taskId,
                contentType,
                file,
            });

            // 设置最终进度为100%
            if (onProgress) {
                onProgress(100);
            }

            // 映射解析结果到识别结果
            return this._mapParseResponseToRecognitionResult(parseResponse);
        } catch (error) {
            // 更新进度为失败状态
            if (onProgress) {
                onProgress(0);
            }
            throw error;
        }
    }

    /**
     * 模拟识别进度
     * 注意：当前API不支持真实的进度跟踪，这里提供基本的进度反馈
     */
    private async _simulateProgress(onProgress?: (progress: number) => void): Promise<void> {
        if (!onProgress) return;

        // 立即显示开始识别
        onProgress(0);

        // 使用 Promise 来处理异步进度更新，确保测试可以等待
        await new Promise<void>((resolve) => {
            let progress = 0;
            const updateProgress = () => {
                if (progress === 0) {
                    progress = 25;
                    onProgress(25);
                    setTimeout(updateProgress, 50);
                } else if (progress === 25) {
                    progress = 50;
                    onProgress(50);
                    setTimeout(updateProgress, 50);
                } else if (progress === 50) {
                    progress = 75;
                    onProgress(75);
                    setTimeout(() => resolve(), 50);
                }
            };
            setTimeout(updateProgress, 50);
        });
    }

    /**
     * 解析文件ID，提取任务ID和文件名
     * 文件ID格式：{taskId}_{timestamp}_{fileName}
     */
    private _parseFileId(fileId: string): { taskId: string; fileName: string } {
        // 首先尝试从存储的文件ID格式解析
        const parts = fileId.split('_');
        if (parts.length >= 3) {
            const taskId = parts[0];
            const fileName = parts.slice(2).join('_'); // 重新连接文件名部分
            return { taskId, fileName };
        }

        // 如果解析失败，使用当前任务ID
        const currentTaskId = this._currentTaskService.taskId;
        if (!currentTaskId) {
            throw new Error('无法确定任务ID');
        }

        return {
            taskId: currentTaskId,
            fileName: fileId, // 假设fileId就是文件名
        };
    }


    /**
     * 将解析响应映射到识别结果
     */
    private _mapParseResponseToRecognitionResult(response: DocumentParseResponse): RecognitionResult {
        // 获取文本内容
        const extractedText = response.text_content || '';

        // 获取图片数量
        const imageCount = response.images?.length || 0;

        return {
            imageCount,
            extractedText,
        };
    }
}
